import React, { useCallback, useMemo, useRef } from 'react';
import { ScrollView, Pressable, View, type LayoutChangeEvent } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { addDays, dayKey, formatMatchDate, isSameDay } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

const DAYS_AROUND_TODAY = 7;

/** Horizontal date selector centered on today (-7 … +7 days). */
export function DateStrip({
  selected,
  onSelect,
}: {
  selected: Date;
  onSelect: (date: Date) => void;
}) {
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const scrollRef = useRef<ScrollView>(null);
  const centeredOnce = useRef(false);

  const days = useMemo(() => {
    const today = new Date();
    const list: Date[] = [];
    for (let offset = -DAYS_AROUND_TODAY; offset <= DAYS_AROUND_TODAY; offset += 1) {
      list.push(addDays(today, offset));
    }
    return list;
  }, []);

  const today = new Date();

  // Open with today in view instead of a week in the past.
  const onTodayLayout = useCallback((event: LayoutChangeEvent) => {
    if (centeredOnce.current) return;
    centeredOnce.current = true;
    const { x } = event.nativeEvent.layout;
    scrollRef.current?.scrollTo({ x: Math.max(0, x - 60), animated: false });
  }, []);

  return (
    <ScrollView
      ref={scrollRef}
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{ paddingHorizontal: 16, gap: 8 }}
    >
      {days.map((day) => {
        const isSelected = isSameDay(day, selected);
        const isToday = isSameDay(day, today);
        const label = isToday
          ? t('common.today')
          : isSameDay(day, addDays(today, -1))
            ? t('common.yesterday')
            : isSameDay(day, addDays(today, 1))
              ? t('common.tomorrow')
              : formatMatchDate(day, numeralOptions);
        return (
          <Pressable
            key={dayKey(day)}
            accessibilityRole="button"
            accessibilityState={{ selected: isSelected }}
            onPress={() => onSelect(day)}
            onLayout={isToday ? onTodayLayout : undefined}
            className={`rounded-full border px-4 py-2 ${
              isSelected ? 'border-volt bg-volt/15' : 'border-line bg-surface'
            }`}
          >
            <View className="flex-row items-center gap-1.5">
              {isToday ? <View className="h-1.5 w-1.5 rounded-full bg-volt" /> : null}
              <AppText
                variant="micro"
                weight={isSelected ? 'bold' : 'medium'}
                className={isSelected ? 'text-volt' : 'text-muted'}
              >
                {label}
              </AppText>
            </View>
          </Pressable>
        );
      })}
    </ScrollView>
  );
}
